import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { CookieStorageService } from '../cookie-storage.service';

export interface SocketReadyEvent {
  connected: boolean;
  userId: string;
  companyId: string;
}

export interface AppNotification {
  id: string;
  category: 'property' | 'report' | 'system' | string;
  type: string;
  status?: 'processing' | 'created' | 'failed' | string;
  title: string;
  message: string;
  createdAt: string;
  readStatus: boolean;

  address?: string;
  pmxPropertyId?: number | string;

  reportId?: string;
  reportReferenceId?: string;
  reportName?: string;
  failureReason?: string;
  actionUrl?: string;
  clientRequestId?: string;

  recipientUserId?: string;
  companyId?: string;

  data?: any;

  [key: string]: any;
}

export interface ReportStatusNotification {
  notificationId: string;
  recipientUserId: string;
  companyId: string;
  category: 'report';

  type:
  | 'PRICE_RANGE_REPORT'
  | 'FINAL_VALUE_REPORT'
  | 'APPRAISER_REPORT'
  | 'priceRange'
  | 'finalValue'
  | 'appraiserReport';

  status: 'processing' | 'created' | 'success' | 'failed';

  title: string;
  message: string;
  failureReason?: string;

  reportId: string;
  reportReferenceId?: string;
  reportName: string;
  actionUrl?: string;
  clientRequestId?: string;

  readStatus: boolean;
  createdDate: string;
}

export interface NotificationStatusUpdatedEvent {
  notificationIds: string[];
  readStatus: boolean;
  updatedAt: string;
  category?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService implements OnDestroy {
  private socket: Socket;
  private enabled = false;
  private recoveryHandler?: () => Observable<boolean>;
  private recoverySubscription?: Subscription;
  private recovering = false;
  private recoveryAttempted = false;
  private recoveryFailures = 0;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private lifecycle = 0;
  private handshakeToken = '';
  private blockedToken: string | null = null;
  private resyncSubject = new Subject<void>();
  private recoveryErrorSubject = new Subject<unknown>();

  // Consumers must fetch current server state; missed events are not replayed.
  readonly resyncRequired$ = this.resyncSubject.asObservable();
  readonly recoveryErrors$ = this.recoveryErrorSubject.asObservable();

  private readonly resumeListener = () => {
    if (!this.enabled || (typeof document !== 'undefined' && document.hidden)) return;
    this.runInsideAngular(() => {
      this.resyncSubject.next();
      this.connect();
    });
  };

  private connectedSubject =
    new BehaviorSubject<boolean>(false);

  private readySubject =
    new Subject<SocketReadyEvent>();

  private notificationSubject =
    new Subject<AppNotification>();

  private notificationStatusSubject =
    new Subject<NotificationStatusUpdatedEvent>();

  readonly connected$ =
    this.connectedSubject.asObservable();

  readonly ready$ =
    this.readySubject.asObservable();

  /**
   * Common stream for report, property and system notifications.
   */
  readonly notifications$ =
    this.notificationSubject.asObservable();

  /**
   * Common stream for notification read-status changes.
   */
  readonly notificationStatusUpdated$ =
    this.notificationStatusSubject.asObservable();

  constructor(
    private cookieStorageService: CookieStorageService,
    private ngZone: NgZone
  ) {
    /*
     * Local testing.
     *
     * For QA/production, replace this with:
     *
     * const socketUrl =
     *   environment.prospektrBackendBaseURL.replace(/\/$/, '');
     *
     * Also import:
     * import { environment } from 'src/environments/environment';
     */

    const socketUrl = environment.prospektrBackendBaseURL.replace(/\/$/, '');

    // const socketUrl = 'http://localhost:3000';

    this.socket = io(socketUrl, {
      path: '/socket.io',
      autoConnect: false,

      /*
       * WebSocket-only mode avoids polling requests reaching
       * different ECS instances when sticky sessions are unavailable.
       */
      transports: ['websocket'],

      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
      timeout: 20000,

      /*
       * This callback is executed whenever Socket.IO attempts
       * to establish or re-establish the connection.
       */
      auth: (callback) => {
        this.handshakeToken = this.cookieStorageService.getCookieValue('idToken') || '';
        callback({ idToken: this.handshakeToken });
      },
    });

    this.registerSocketListeners();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.resumeListener);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.resumeListener);
    }
  }

  /**
   * Connect after the user has authenticated and the ID token
   * has been saved.
   */
  connect(): void {
    this.enabled = true;
    if (this.socket.connected || this.recovering || this.retryTimer) return;
    const token = this.cookieStorageService.getCookieValue('idToken') || '';
    if (this.blockedToken === token) return;
    if (!token) {
      if (this.cookieStorageService.getCookieValue('refreshToken')) {
        this.recoverAuthentication();
      }
      return;
    }
    this.socket.connect();
  }

  /** Called by the interceptor; callback avoids a Socket -> HttpClient DI cycle. */
  registerAuthenticationRecovery(handler: () => Observable<boolean>): void {
    this.recoveryHandler = handler;
  }

  /** Refresh must not resurrect a socket explicitly disconnected during logout. */
  tokensUpdated(): void {
    if (!this.enabled || this.recovering) return;
    const token = this.cookieStorageService.getCookieValue('idToken') || '';
    if (token !== this.handshakeToken) {
      this.blockedToken = null;
      this.recoveryAttempted = false;
      this.recoveryFailures = 0;
      if (this.retryTimer) clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
      this.socket.disconnect();
    }
    this.connect();
  }

  disconnect(): void {
    this.enabled = false;
    this.lifecycle++;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.recoverySubscription?.unsubscribe();
    this.recoverySubscription = undefined;
    this.recovering = false;
    this.recoveryAttempted = false;
    this.recoveryFailures = 0;
    this.blockedToken = null;
    this.socket.disconnect();
    this.runInsideAngular(() => this.connectedSubject.next(false));
  }

  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  private recoverAuthentication(): void {
    if (!this.enabled || this.recovering || this.recoveryAttempted) return;
    if (!this.recoveryHandler) {
      this.recoveryErrorSubject.next(new Error('Authentication recovery is not registered.'));
      return;
    }
    this.recovering = true;
    this.recoveryAttempted = true;
    this.socket.disconnect();
    const lifecycle = this.lifecycle;
    // Parent subscription also handles a synchronously completing callback.
    const subscription = new Subscription();
    this.recoverySubscription = subscription;
    subscription.add(this.recoveryHandler().pipe(take(1)).subscribe({
      next: (authenticated) => {
        if (!this.enabled || lifecycle !== this.lifecycle) return;
        this.recovering = false;
        if (!authenticated) {
          this.blockedToken = this.cookieStorageService.getCookieValue('idToken') || '';
          this.recoveryErrorSubject.next(new Error('Authentication was not completed.'));
          return;
        }
        this.blockedToken = null;
        // Keep recoveryAttempted until connect succeeds: no refresh/reject loop.
        this.connect();
      },
      error: (error: any) => {
        if (!this.enabled || lifecycle !== this.lifecycle) return;
        this.recovering = false;
        this.recoveryErrorSubject.next(error);
        // Network/server refresh failures do not imply that the session expired.
        const transient = error?.status === 0 || error?.status === 429 ||
          error?.status >= 500 || error?.name === 'TimeoutError';
        if (transient && this.recoveryFailures < 3) {
          const delay = 2000 * Math.pow(2, this.recoveryFailures++);
          this.retryTimer = setTimeout(() => {
            this.retryTimer = undefined;
            if (!this.enabled || lifecycle !== this.lifecycle) return;
            this.recoveryAttempted = false;
            this.recoverAuthentication();
          }, delay);
        } else {
          this.blockedToken = this.cookieStorageService.getCookieValue('idToken') || '';
        }
      },
      complete: () => {
        if (lifecycle === this.lifecycle) this.recovering = false;
      },
    }));
  }

  ngOnDestroy(): void {
    this.disconnect();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.resumeListener);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.resumeListener);
    }
  }

  isConnected(): boolean {
    return this.socket.connected;
  }

  /**
   * Subscribe to any custom Socket.IO event.
   *
   * Events registered through this method are also executed inside
   * Angular's zone so UI bindings update immediately.
   */
  on<T>(eventName: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      const listener = (data: T) => {
        this.runInsideAngular(() => {
          subscriber.next(data);
        });
      };

      this.socket.on(eventName, listener);

      return () => {
        this.socket.off(eventName, listener);
      };
    });
  }

  /**
   * Ensure state changes caused by Socket.IO trigger Angular
   * change detection.
   */
  private runInsideAngular(
    callback: () => void
  ): void {
    if (NgZone.isInAngularZone()) {
      callback();
      return;
    }

    this.ngZone.run(callback);
  }

  private registerSocketListeners(): void {
    this.socket.on('connect', () => {
      if (!this.enabled) { this.socket.disconnect(); return; }
      this.recoveryAttempted = false;
      this.recoveryFailures = 0;
      this.blockedToken = null;
      // console.log(
      //   'Socket connected:',
      //   this.socket.id
      // );

      this.runInsideAngular(() => {
        this.connectedSubject.next(true);
      });
    });

    this.socket.on(
      'socket:ready',
      (event: SocketReadyEvent) => {
        // console.log(
        //   'Socket authentication successful:',
        //   event
        // );

        this.runInsideAngular(() => {
          this.readySubject.next(event);
          this.resyncSubject.next();
        });
      }
    );

    /**
     * Common backend event for report, property and system
     * notifications.
     */
    this.socket.on(
      'notification:new',
      (notification: AppNotification) => {
        console.log(
          'Notification received:',
          notification
        );

        const normalizedNotification =
          this.normalizeAppNotification(notification);

        this.runInsideAngular(() => {
          this.notificationSubject.next(
            normalizedNotification
          );
        });
      }
    );

    /**
     * Existing report-specific backend event.
     *
     * It is converted into the common AppNotification structure
     * and published through notifications$.
     */
    this.socket.on(
      'report:status-changed',
      (
        reportNotification:
          ReportStatusNotification
      ) => {
        // console.log(
        //   'Report notification received:',
        //   reportNotification
        // );

        const normalizedNotification =
          this.normalizeReportNotification(
            reportNotification
          );

        this.runInsideAngular(() => {
          this.notificationSubject.next(
            normalizedNotification
          );
        });
      }
    );

    /**
     * Common notification read-status event.
     */
    this.socket.on(
      'notification:status-updated',
      (
        event: NotificationStatusUpdatedEvent
      ) => {
        this.runInsideAngular(() => {
          this.notificationStatusSubject.next(
            event
          );
        });
      }
    );

    /**
     * Compatibility with the existing property notification
     * status event.
     */
    this.socket.on(
      'property-notifications:status-updated',
      (
        event: NotificationStatusUpdatedEvent
      ) => {
        const normalizedEvent: NotificationStatusUpdatedEvent = {
          ...event,
          category: event.category || 'property',
        };

        this.runInsideAngular(() => {
          this.notificationStatusSubject.next(
            normalizedEvent
          );
        });
      }
    );

    this.socket.on(
      'disconnect',
      (reason: string) => {
        console.log(
          'Socket disconnected:',
          reason
        );

        this.runInsideAngular(() => {
          this.connectedSubject.next(false);
        });
      }
    );

    this.socket.on(
      'connect_error',
      (error: Error & { data?: { code?: string } }) => {
        console.error(
          'Socket connection failed:',
          error.message
        );

        this.runInsideAngular(() => {
          this.connectedSubject.next(false);
          if (!this.enabled) return;
          const code = error.data?.code;
          const recoverable = code === 'TOKEN_EXPIRED' || code === 'TOKEN_MISSING' ||
            (!code && error.message === 'Authentication failed');
          if (recoverable) {
            // A refresh may have completed while the old handshake was pending.
            const currentToken = this.cookieStorageService.getCookieValue('idToken') || '';
            if (currentToken && currentToken !== this.handshakeToken) {
              this.socket.connect();
            } else if (this.recoveryAttempted) {
              this.blockedToken = currentToken;
              this.recoveryErrorSubject.next(error);
            } else {
              this.recoverAuthentication();
            }
          } else if (code) {
            // Origin/account/configuration errors must not trigger refresh/login loops.
            this.blockedToken = this.handshakeToken;
            this.recoveryErrorSubject.next(error);
          }
          // Ordinary transport failures use Socket.IO's built-in backoff.
        });
      }
    );
  }

  /**
   * Normalize notification:new payloads from different backend
   * notification sources.
   */
  private normalizeAppNotification(
    notification: AppNotification
  ): AppNotification {
    const createdAt =
      notification.createdAt ||
      notification.createdDate ||
      new Date().toISOString();

    const notificationId =
      notification.id ||
      notification.notificationId ||
      notification._id ||
      '';

    return {
      ...notification,

      id: notificationId.toString(),

      category:
        notification.category || 'system',

      type:
        notification.type ||
        notification.referenceType ||
        'GENERAL',

      title:
        notification.title ||
        'New notification',

      message:
        notification.message ||
        notification.notificationMessage ||
        notification.displayText ||
        'New notification',

      createdAt,

      /*
       * Only the explicit boolean true value is considered read.
       * Missing or false values remain unread.
       */
      readStatus:
        notification.readStatus === true,
    };
  }

  /**
   * Convert the report-specific event into the shared
   * AppNotification format.
   */
  private normalizeReportNotification(
    notification: ReportStatusNotification
  ): AppNotification {
    return {
      id:
        notification.notificationId?.toString() ||
        '',

      category: 'report',
      type: notification.type,
      status: notification.status,

      title:
        notification.title ||
        'Report status updated',

      message:
        notification.message ||
        'Your report status has been updated.',

      createdAt:
        notification.createdDate ||
        new Date().toISOString(),

      readStatus:
        notification.readStatus === true,

      reportId:
        notification.reportId,

      reportReferenceId:
        notification.reportReferenceId,

      reportName:
        notification.reportName,

      failureReason:
        notification.failureReason,

      actionUrl:
        notification.actionUrl,

      recipientUserId:
        notification.recipientUserId,

      companyId:
        notification.companyId,

      clientRequestId:
        notification.clientRequestId,
    };
  }
}